import { createAsyncThunk } from '@reduxjs/toolkit';

import { endpoints } from '../../utils/endpoints';
import { fetchJSON, ResponseError } from '../../utils/fetch';
import { logError } from '../../utils/logging';
import { getUtmCodeParams, UtmCodeParams } from '../../utils/url';
import {
  CompleteOrderRequestProps,
  FinalizeOrderResponseBody,
  OrderSummaryPayloadType,
  ValidatePromoCodeType,
  WaitlistPayloadType,
} from './cart-types';

const get = endpoints();

const validatePromoCode = createAsyncThunk(
  'cart/validatePromoCode',
  async (payload: ValidatePromoCodeType) => {
    const { eventSlug, promoCode } = payload;

    return await fetchJSON(get('VALIDATE_PROMO_CODE')(eventSlug, promoCode), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });
  },
);

export const calculateOrderSummaryRequest = (
  payload: OrderSummaryPayloadType,
) => {
  const {
    eventSlug,
    orderId,
    tickets,
    promoCode,
    email,
    first_name,
    last_name,
  } = payload;

  const body = {
    tickets: tickets,
    email: email || 'registrations@hopin.to',
    first_name: first_name || 'first_name_placeholder',
    last_name: last_name || 'last_name_placeholder',
    ...(orderId && { id: orderId }),
    ...(promoCode && { promo_code: promoCode }),
  };

  return fetchJSON(get('ORDER_SUMMARY')(eventSlug), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body,
  });
};

const calculateOrderSummary = createAsyncThunk(
  'cart/calculateOrderSummary',
  async (payload: OrderSummaryPayloadType, { rejectWithValue }) => {
    try {
      return await calculateOrderSummaryRequest(payload);
    } catch (error) {
      logError(error as any);
      const responseBody = (error as ResponseError)?.response?.body;
      if (responseBody) {
        return rejectWithValue(responseBody);
      }
      return rejectWithValue({});
    }
  },
);

const getOnWaitlist = createAsyncThunk(
  'cart/getOnWaitlist',
  async (payload: WaitlistPayloadType, { rejectWithValue }) => {
    const { ticketId, firstName, lastName, email } = payload;
    try {
      return await fetchJSON(get('WAITLIST')(ticketId), {
        body: {
          user_email: email,
          user_first_name: firstName,
          user_last_name: lastName,
          external_id: ticketId,
        },
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      });
    } catch (error) {
      logError(error as any);
      const responseBody = (error as ResponseError)?.response?.body;
      if (responseBody) {
        return rejectWithValue(responseBody);
      }
      return rejectWithValue({});
    }
  },
);

export const fetchInvites = (eventSlug: string, orderSlug: string) =>
  fetchJSON(get('ORDER_INVITES')(eventSlug, orderSlug), {
    headers: {
      Accept: 'application/json',
    },
  });

export const completeOrder = async ({
  eventSlug,
  token,
  stripeId,
  widgetId,
  source,
  status,
  customerId,
  orderId,
  acceptedTerms = false,
}: CompleteOrderRequestProps) => {
  const headers: HeadersInit = {
    Accept: 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const body: Record<
      string,
      string | number | UtmCodeParams | boolean | null
    > = {
      id: orderId,
      customer_id: customerId,
      status: status,
      widget_id: widgetId,
      source,
      accepted_terms: acceptedTerms,
    };

    if (stripeId) {
      body.stripe_id = stripeId;
    }
    body.utm_details = getUtmCodeParams(window.location.search);
    const finalizeOrderResponse = await fetchJSON(
      get('FINALIZE_ORDER')(eventSlug),
      {
        method: 'POST',
        headers: headers,
        body,
      },
    );

    const { slug: orderSlug, auth_registered: authRegistered } =
      finalizeOrderResponse as FinalizeOrderResponseBody;

    return {
      success: true,
      orderSlug,
      authRegistered,
    };
  } catch (err) {
    logError(err as any);
    return {
      success: false,
      orderSlug: '',
      authRegistered: false,
    };
  }
};

export { calculateOrderSummary, getOnWaitlist, validatePromoCode };
